import { Button, Container, Dropdown, Form, Modal } from "react-bootstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { useState } from "react";
import { auth } from "../firebaseConfig";
import { useAppDispatch, useAppSelector } from "../store";
import { companyActions } from "../store/company-slice";
import { removeUser } from "../store/user-slice";
import { sendMagicLink, signInWithGoogle } from "../utils/auth";
import "./Header.css";

const getEnvLabel = (envName: string): string => {
  const labels: Record<string, string> = {
    staging: "stg",
    development: "dev",
    localhost: "local",
  };
  return labels[envName] || "";
};

function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const user = useAppSelector((state) => state.user);
  const activeCompany = useAppSelector((state) => state.company.activeCompany);

  const currentTemplatePath = useAppSelector((state) => state.templates.selectedPath);
  const currentTemplateVersion = useAppSelector((state) => state.templates.selectedVersion);

  const [tokenCopyMessage, setTokenCopyMessage] = useState("");

  const [showMagicLinkModal, setShowMagicLinkModal] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicLinkSending, setMagicLinkSending] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const openMagicLinkModal = () => {
    setMagicLinkEmail("");
    setMagicLinkSent(false);
    setShowMagicLinkModal(true);
  };

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setMagicLinkSending(true);
    try {
      await sendMagicLink(magicLinkEmail);
      // The backend always responds 200 to avoid leaking membership info, so we
      // show a neutral confirmation regardless of whether an email was sent.
      setMagicLinkSent(true);
    } catch (error) {
      console.error("Error requesting magic link:", error);
      // Show the same confirmation to avoid leaking whether the email exists.
      setMagicLinkSent(true);
    } finally {
      setMagicLinkSending(false);
    }
  };

  const handleCopyToken = async () => {
    try {
      const currentToken = await auth?.currentUser?.getIdToken();

      if (!currentToken) {
        setTokenCopyMessage("No token available");
        setTimeout(() => setTokenCopyMessage(""), 3000);
        return;
      }

      const tokenResult = await auth?.currentUser?.getIdTokenResult();
      const expirationTime = new Date(tokenResult?.expirationTime || "");
      const now = new Date();
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

      let tokenToCopy = currentToken;
      let message = "Token copied!";

      if (expirationTime < fiveMinutesFromNow) {
        const freshToken = await auth?.currentUser?.getIdToken(true);
        tokenToCopy = freshToken || currentToken;
        message = "Token refreshed and copied!";
      }

      await navigator.clipboard.writeText(tokenToCopy);
      setTokenCopyMessage(message);
      setTimeout(() => setTokenCopyMessage(""), 3000);
    } catch (error) {
      console.error("Error copying token:", error);
      setTokenCopyMessage("Failed to copy token");
      setTimeout(() => setTokenCopyMessage(""), 3000);
    }
  };

  const handleLogout = () => {
    dispatch(removeUser());
    dispatch(companyActions.setActiveCompany(null));
    navigate("/");
  };

  const envName = (window as any)._env_.REACT_APP_ENV;
  const envLabel = getEnvLabel(envName);
  const isProduction = !envLabel;

  let templateSuffix = "/";
  if (currentTemplatePath && currentTemplatePath.name) {
    templateSuffix = `/${currentTemplatePath.namespace}/${currentTemplatePath.type}/${currentTemplatePath.name}/${currentTemplateVersion}`;
  }

  const navLinks = (
    <>
      <NavLink to="/" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")} end>
        Home
      </NavLink>
      {user?.roles?.includes("companies-view") && (
        <NavLink to="/companies" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          Companies
        </NavLink>
      )}
      {activeCompany && user?.roles?.includes("entity-mgmt") && (
        <NavLink to="/entities" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          Entities
        </NavLink>
      )}
      {activeCompany && user?.roles?.includes("templates") && (
        <>
          <NavLink to="/mail-templates/nl" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            Mail Templates (v3)
          </NavLink>
          {false && (
            <NavLink
              to={`/templates${templateSuffix}`}
              className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
            >
              Templates (v4)
            </NavLink>
          )}
        </>
      )}
      {(user?.roles?.includes("token-copy") || user?.roles?.includes("admin-endpoints")) && (
        <NavLink to="/admin" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
          Admin
        </NavLink>
      )}
    </>
  );

  return (
    <div className="watchtower-header-wrapper">
      {/* Environment Bar */}
      <div
        className="watchtower-env-bar"
        style={{ backgroundColor: `${(window as any)._env_.REACT_APP_HEADER_BGCOLOR}` }}
      >
        <Container className="d-flex align-items-center justify-content-center">
          {!isProduction && (
            <span>
              You are currently working on the <strong>{envLabel}</strong> environment
            </span>
          )}
          {!isProduction && activeCompany && <span className="watchtower-env-bullet">•</span>}
          {activeCompany && (
            <span>
              You are maintaining{" "}
              <Link to={`/companies/${activeCompany?.name}`} className="watchtower-env-company">
                {activeCompany?.info?.name || activeCompany?.name}
              </Link>
              <Link to="/companies" className="watchtower-env-switch" title="Switch customer">
                <i className="bi bi-arrow-left-right"></i>
              </Link>
            </span>
          )}
        </Container>
      </div>

      {/* Main Header */}
      <nav className="watchtower-header">
        <Container className="watchtower-header-container">
          {/* Logo */}
          <Link to="/" className="watchtower-brand">
            <img src="/img/colibrylogo.svg" width="120" />
          </Link>

          {user?.email ? (
            <>
              {/* Desktop: nav links + user info + menu */}
              <div className="watchtower-right-section">
                <div className="watchtower-nav-links d-none d-lg-flex">{navLinks}</div>

                <div className="watchtower-user-info d-none d-sm-flex">
                  <i className="bi bi-person"></i>
                  <span>{user.email}</span>
                </div>

                <Dropdown align="end">
                  <Dropdown.Toggle variant="link" id="menu-dropdown" className="watchtower-menu-toggle">
                    <i className="bi bi-list" style={{ fontSize: "1.5rem" }}></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {/* Mobile nav links inside dropdown */}
                    <div className="d-lg-none">
                      {user?.roles?.includes("companies-view") && (
                        <Dropdown.Item as={Link} to="/companies">
                          Companies
                        </Dropdown.Item>
                      )}
                      {activeCompany && user?.roles?.includes("entity-mgmt") && (
                        <Dropdown.Item as={Link} to="/entities">
                          Entities
                        </Dropdown.Item>
                      )}
                      {activeCompany && user?.roles?.includes("templates") && (
                        <Dropdown.Item as={Link} to="/mail-templates/nl">
                          Mail Templates (v3)
                        </Dropdown.Item>
                      )}
                      {(user?.roles?.includes("token-copy") || user?.roles?.includes("admin-endpoints")) && (
                        <Dropdown.Item as={Link} to="/admin">
                          Admin
                        </Dropdown.Item>
                      )}
                      <Dropdown.Divider />
                    </div>
                    {user?.roles?.includes("token-copy") && (
                      <>
                        <Dropdown.Item onClick={handleCopyToken}>
                          <i className="bi bi-clipboard me-2"></i>
                          Copy Token
                          {tokenCopyMessage && (
                            <span className="ms-2 text-success" style={{ fontSize: "0.85em" }}>
                              {tokenCopyMessage}
                            </span>
                          )}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                      </>
                    )}
                    <Dropdown.Item onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right me-2"></i>
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </>
          ) : (
            <Dropdown align="end" className="ms-auto">
              <Dropdown.Toggle variant="primary" id="login-dropdown">
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Login
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={signInWithGoogle}>
                  <i className="bi bi-google me-2"></i>
                  Login using Google
                </Dropdown.Item>
                <Dropdown.Item onClick={openMagicLinkModal}>
                  <i className="bi bi-magic me-2"></i>
                  Login using Magic Link
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Container>
      </nav>

      {/* Magic Link Login Modal */}
      <Modal show={showMagicLinkModal} onHide={() => setShowMagicLinkModal(false)} centered>
        <Form onSubmit={handleSendMagicLink}>
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="bi bi-magic me-2"></i>
              Login using Magic Link
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {magicLinkSent ? (
              <p className="text-muted mb-0">
                <i className="bi bi-check-circle text-success me-2"></i>
                If <strong>{magicLinkEmail}</strong> has an account, a magic link is on its way. Check
                your inbox and click the link to sign in &mdash; it's valid for <strong>6 hours</strong>.
              </p>
            ) : (
              <>
                <p className="text-muted">
                  Enter your email address and we'll send you a magic link to sign in (assuming you have
                  an account on our platform). No password needed &mdash; just click the link in your
                  inbox. The link is valid for <strong>6 hours</strong>, after which you'll need to
                  request a new one.
                </p>
                <Form.Group controlId="magicLinkEmail">
                  <Form.Label>Email address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="you@example.com"
                    value={magicLinkEmail}
                    onChange={(e) => setMagicLinkEmail(e.target.value)}
                    autoFocus
                    required
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            {magicLinkSent ? (
              <Button variant="primary" onClick={() => setShowMagicLinkModal(false)}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setShowMagicLinkModal(false)}
                  disabled={magicLinkSending}
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" disabled={magicLinkSending}>
                  <i className="bi bi-send me-2"></i>
                  {magicLinkSending ? "Sending..." : "Send magic link"}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default Header;
